/**
 * Subscription Slice
 * Manages subscription state and RevenueCat integration
 */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  SubscriptionState,
  UserAccessInfo,
  RevenueCatCustomerInfo,
} from "@/types/subscription.types";
import apiClient from "@/services/api/apiClient";
import { createStripePortalSession } from "@/services/api/endpoints/stripe.api";
import { logger } from "@/utils/logger";

/**
 * Fetch user access info from backend
 */
export const fetchUserAccess = createAsyncThunk(
  "subscription/fetchUserAccess",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<UserAccessInfo>(
        "/teacher/user/access"
      );
      return response;
    } catch (error: any) {
      // Let the apiClient interceptor handle auth errors (401)
      // Don't re-throw to avoid interfering with redirect
      return rejectWithValue(error.message || "Failed to fetch user access");
    }
  }
);

/**
 * Fetch RevenueCat customer info
 * Can be called with customerInfo parameter (from hook) or without (direct SDK call)
 */
export const fetchRevenueCatStatus = createAsyncThunk<
  { entitlements: Record<string, any>; hasActiveSubscription: boolean },
  RevenueCatCustomerInfo | undefined,
  { rejectValue: string }
>(
  "subscription/fetchRevenueCatStatus",
  async (customerInfo, { rejectWithValue }) => {
    try {
      let info: any;

      if (customerInfo) {
        // Use provided customer info (from hook)
        info = customerInfo;
      } else {
        // Fetch customer info directly from RevenueCat SDK
        const { Purchases } = await import("@revenuecat/purchases-js");
        const sdkInfo: any =
          await Purchases.getSharedInstance().getCustomerInfo();
          
        // Helper to serialize entitlements
        const serializeEntitlements = (ents: Record<string, any>) => {
          const serialized: Record<string, any> = {};
          if (!ents) return serialized;
          Object.keys(ents).forEach((key) => {
            const ent = ents[key];
            serialized[key] = {
              ...ent,
              latestPurchaseDate: ent.latestPurchaseDate instanceof Date ? ent.latestPurchaseDate.toISOString() : ent.latestPurchaseDate,
              originalPurchaseDate: ent.originalPurchaseDate instanceof Date ? ent.originalPurchaseDate.toISOString() : ent.originalPurchaseDate,
              expirationDate: ent.expirationDate instanceof Date ? ent.expirationDate.toISOString() : ent.expirationDate,
            };
          });
          return serialized;
        };

        // Map SDK CustomerInfo to our RevenueCatCustomerInfo type
        // Note: SDK CustomerInfo properties may differ from our interface
        info = {
          entitlements: {
            active: serializeEntitlements(sdkInfo.entitlements?.active || {}),
            all: serializeEntitlements(sdkInfo.entitlements?.all || {}),
          },
          activeSubscriptions: sdkInfo.activeSubscriptions || [],
          allPurchasedProductIdentifiers:
            sdkInfo.allPurchasedProductIdentifiers || [],
          latestExpirationDate: sdkInfo.latestExpirationDate instanceof Date ? sdkInfo.latestExpirationDate.toISOString() : sdkInfo.latestExpirationDate,
          firstSeen: sdkInfo.firstSeen instanceof Date ? sdkInfo.firstSeen.toISOString() : (sdkInfo.firstSeen || new Date().toISOString()),
          originalPurchaseDate: sdkInfo.originalPurchaseDate instanceof Date ? sdkInfo.originalPurchaseDate.toISOString() : sdkInfo.originalPurchaseDate,
          managementURL: sdkInfo.managementURL,
          originalAppUserId: sdkInfo.originalAppUserId || "",
        };
      }

      // Process RevenueCat customer info
      // SECURITY: Use .active to get ONLY active entitlements (not expired ones)
      // Using .all would include expired subscriptions, causing security issues
      const rawEntitlements = info.entitlements.active || {};
      
      // Serialize dates to prevent Redux non-serializable value errors
      const entitlements: Record<string, any> = {};
      Object.keys(rawEntitlements).forEach((key) => {
        const ent = rawEntitlements[key];
        entitlements[key] = {
          ...ent,
          latestPurchaseDate: ent.latestPurchaseDate instanceof Date ? ent.latestPurchaseDate.toISOString() : ent.latestPurchaseDate,
          originalPurchaseDate: ent.originalPurchaseDate instanceof Date ? ent.originalPurchaseDate.toISOString() : ent.originalPurchaseDate,
          expirationDate: ent.expirationDate instanceof Date ? ent.expirationDate.toISOString() : ent.expirationDate,
        };
      });

      logger.log("[Redux] Processed active entitlements:", entitlements);

      return {
        entitlements,
        hasActiveSubscription: Object.keys(entitlements).length > 0,
      };
    } catch (error: any) {
      logger.error("[Redux] Error in fetchRevenueCatStatus:", error);
      logger.error("[Redux] Error message:", error.message);
      logger.error("[Redux] Error stack:", error.stack);
      return rejectWithValue(
        error.message || "Failed to process RevenueCat status"
      );
    }
  }
);

/**
 * Update free access counters
 */
export const updateFreeAccess = createAsyncThunk(
  "subscription/updateFreeAccess",
  async (
    data: { qty_free_access: number; max_free_access: number },
    { rejectWithValue }
  ) => {
    try {
      // This could be an API call to update free access on backend
      // For now, just return the data to update local state
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update free access");
    }
  }
);

/**
 * Create Stripe Customer Portal Session
 */
export const createPortalSession = createAsyncThunk<
  { url: string },
  { customerId: string; returnUrl: string },
  { rejectValue: string }
>(
  "subscription/createPortalSession",
  async ({ customerId, returnUrl }, { rejectWithValue }) => {
    try {
      const result = await createStripePortalSession(customerId, returnUrl);
      return result;
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to create portal session"
      );
    }
  }
);

/**
 * Initial state
 */
const initialState: SubscriptionState = {
  entitlements: {},
  hasFreeAccess: false,
  qty_free_access: 0,
  max_free_access: 0,
  status: "idle",
  error: null,
  revenueCatLoaded: false,
  accessLoaded: false,
};

/**
 * Subscription slice
 */
const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    // Reset subscription state
    resetSubscription: (state) => {
      state.entitlements = {};
      state.hasFreeAccess = false;
      state.qty_free_access = 0;
      state.max_free_access = 0;
      state.status = "idle";
      state.error = null;
      state.revenueCatLoaded = false;
      state.accessLoaded = false;
      state.userAccess = undefined;
    },

    // Set RevenueCat loaded status
    setRevenueCatLoaded: (state, action: PayloadAction<boolean>) => {
      state.revenueCatLoaded = action.payload;
    },

    // Set access loaded status
    setAccessLoaded: (state, action: PayloadAction<boolean>) => {
      state.accessLoaded = action.payload;
    },

    // Update entitlements directly (used by RevenueCat hook)
    updateEntitlements: (state, action: PayloadAction<Record<string, any>>) => {
      state.entitlements = action.payload;
    },

    // Clear error
    clearSubscriptionError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchUserAccess
    builder
      .addCase(fetchUserAccess.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchUserAccess.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.userAccess = action.payload;
        state.accessLoaded = true;
      })
      .addCase(fetchUserAccess.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
        state.accessLoaded = true; // Still mark as loaded even on error
      })

      // fetchRevenueCatStatus
      .addCase(fetchRevenueCatStatus.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchRevenueCatStatus.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.entitlements = action.payload.entitlements;
        state.revenueCatLoaded = true;
      })
      .addCase(fetchRevenueCatStatus.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
        state.revenueCatLoaded = true; // Still mark as loaded even on error
      })

      // updateFreeAccess
      .addCase(updateFreeAccess.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateFreeAccess.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.qty_free_access = action.payload.qty_free_access;
        state.max_free_access = action.payload.max_free_access;
        state.hasFreeAccess =
          action.payload.qty_free_access < action.payload.max_free_access;
      })
      .addCase(updateFreeAccess.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })

      // createPortalSession
      .addCase(createPortalSession.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createPortalSession.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(createPortalSession.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const {
  resetSubscription,
  setRevenueCatLoaded,
  setAccessLoaded,
  updateEntitlements,
  clearSubscriptionError,
} = subscriptionSlice.actions;

// Export reducer
export default subscriptionSlice.reducer;
